<script setup lang="ts">
// Polar / "rose" bar chart — bars laid out radially, one wedge per
// category. Use for cyclical categories (hour-of-day, day-of-week)
// where wrap-around carries meaning. The angular axis is the category;
// the radial axis is the value.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { PolarBarChartSpec } from "../sbi/protocol";
import { COLORS } from "../theme.ts";
import {
  axisLabelDefaults,
  axisLineDefaults,
  chartChrome,
  splitLineDefaults,
  tooltipDefaults,
} from "./echarts-base";
import { charPx, NARROW } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: PolarBarChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

// Rim labels share the circumference: once each category's arc is
// shorter than its label, show every Nth label (truncated at 72px)
// rather than overprint them. Narrow charts also tighten the ring so
// the labels have room outside it.
const angleLabels = computed(() => {
  const categories = rows.value.map((r) => r[props.spec.category]);
  const w = ctx.value.width;
  if (w <= 0 || categories.length === 0) return { categories, label: {} };
  const radius = (Math.min(w, height.value) / 2) * 0.85;
  const arc = (2 * Math.PI * radius) / categories.length;
  const longest = categories.reduce<number>((m, c) => Math.max(m, String(c ?? "").length), 0);
  const labelPx = Math.min(72, longest * charPx());
  return {
    categories,
    label: {
      interval: Math.max(0, Math.ceil(labelPx / Math.max(1, arc)) - 1),
      width: 72,
      overflow: "truncate" as const,
    },
  };
});

const option = computed(() => ({
  ...chartChrome(ctx.value),
  polar: {
    radius: ctx.value.width > 0 && ctx.value.width < NARROW ? ["15%", "72%"] : ["20%", "85%"],
  },
  angleAxis: {
    type: "category" as const,
    data: angleLabels.value.categories,
    ...axisLineDefaults(),
    axisLabel: { ...axisLabelDefaults(), ...angleLabels.value.label },
  },
  radiusAxis: {
    axisLine: { show: false },
    splitLine: { ...splitLineDefaults() },
    axisLabel: { ...axisLabelDefaults() },
  },
  tooltip: tooltipDefaults("item", ctx.value),
  series: [
    {
      type: "bar" as const,
      coordinateSystem: "polar" as const,
      data: rows.value.map((r) => Number(r[props.spec.value])),
      itemStyle: { color: COLORS.primary },
    },
  ],
}));

const height = computed(() =>
  resolveChartHeight("polar_bar", props.spec.height),
);

function onClick(p: { dataIndex?: number; value?: unknown }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const row = rows.value[p.dataIndex];
  if (!row) return;
  emit("click-target", props.spec.id, {
    category: String(row[props.spec.category] ?? ""),
    value: Number(row[props.spec.value]),
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
